import type { EventTemplateDefinition } from "@/lib/event-templates";
import type { TemplatePackage } from "@/lib/template-engine/types";
import { API_ROUTES } from "@/routes";
import apiClient from "./client";

export interface TemplateManagementItem extends EventTemplateDefinition {
  active: boolean;
  premium: boolean;
}

/**
 * Get all templates (superadmin only).
 * Unwraps Laravel Resource collection shape (response.data or response.data.data).
 */
export async function getTemplates(): Promise<TemplateManagementItem[]> {
  const response = await apiClient.get<TemplateManagementItem[] | { data: TemplateManagementItem[] }>(
    API_ROUTES.management.templates.list()
  );

  const raw = response.data;
  if (Array.isArray(raw)) {
    return raw;
  }
  if (raw && typeof raw === "object" && "data" in raw && Array.isArray((raw as { data: TemplateManagementItem[] }).data)) {
    return (raw as { data: TemplateManagementItem[] }).data;
  }
  return [];
}

/**
 * Update template (enable/disable)
 */
export async function updateTemplate(
  id: number,
  data: { active?: boolean }
): Promise<TemplateManagementItem> {
  const response = await apiClient.patch<TemplateManagementItem>(
    API_ROUTES.management.templates.update(id),
    data
  );

  return response.data;
}

/**
 * Preview template
 */
export async function previewTemplate(id: number): Promise<TemplateManagementItem> {
  const response = await apiClient.get<TemplateManagementItem>(
    API_ROUTES.management.templates.preview(id)
  );

  return response.data;
}

/**
 * Uninstall template
 */
export async function uninstallTemplate(id: number): Promise<void> {
  await apiClient.delete(API_ROUTES.management.templates.delete(id));
}

/**
 * Install a template from a .jtpk ZIP package (superadmin only).
 *
 * The backend deliberately only accepts a file upload — installing from a
 * caller-supplied URL is rejected server-side (SSRF surface) — so this
 * client does not offer a URL path either.
 */
export async function installTemplate(
  file: File,
  onProgress?: (percent: number) => void
): Promise<TemplateManagementItem> {
  const formData = new FormData();
  formData.append("file", file);

  const response = await apiClient.post<TemplateManagementItem>(
    API_ROUTES.management.templates.install(),
    formData,
    {
      onUploadProgress: onProgress
        ? (event) => {
            const total = event.total ?? file.size;
            onProgress(total > 0 ? Math.round((event.loaded / total) * 100) : 0);
          }
        : undefined,
    }
  );

  return response.data;
}

/* ------------------------------------------------------------ JUB-161 */

export interface OmittedAsset {
  path: string;
  kind: "asset" | "font" | "preview";
  reason: string;
}

export interface TemplateValidationResult {
  valid: boolean;
  /** Every problem, with the file and field it concerns. Empty when valid. */
  problems: string[];
  key?: string;
  name?: string | null;
  /** Files the manifest declares whose bytes an install does not store. */
  omittedAssets: OmittedAsset[];
  /** Archive entries an install ignores (docs, previews, assets). */
  ignoredFiles: string[];
  install: { action: "create" | "replace_disabled" } | null;
  /** The validated package, for preview. Only present when valid. */
  package: TemplatePackage | null;
}

/**
 * Validate a .jtpk archive without installing it. Package problems are a
 * normal answer (valid=false), not an error.
 */
export async function validateTemplatePackage(file: File): Promise<TemplateValidationResult> {
  const formData = new FormData();
  formData.append("file", file);

  const response = await apiClient.post<TemplateValidationResult>(
    API_ROUTES.management.templates.validate(),
    formData
  );

  return response.data;
}

/** Problems an install refusal carries in error.errors.package, if any. */
export function packageProblemsFromError(error: unknown): string[] {
  const errors = (error as { response?: { data?: { error?: { errors?: { package?: unknown } } } } })?.response?.data?.error
    ?.errors?.package;
  return Array.isArray(errors) ? errors.filter((item): item is string => typeof item === "string") : [];
}

function filenameFrom(disposition: string | undefined, fallback: string): string {
  const match = disposition?.match(/filename\*?=(?:UTF-8'')?"?([^";]+)"?/i);
  return match ? decodeURIComponent(match[1]) : fallback;
}

async function downloadZip(url: string, fallbackName: string): Promise<{ filename: string; omittedAssets: number }> {
  const response = await apiClient.get<Blob>(url, {
    responseType: "blob",
    headers: { Accept: "application/zip" },
  });

  const filename = filenameFrom(response.headers["content-disposition"] as string | undefined, fallbackName);
  const objectUrl = URL.createObjectURL(response.data);
  const link = document.createElement("a");
  link.href = objectUrl;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  link.remove();
  setTimeout(() => URL.revokeObjectURL(objectUrl), 1000);

  const omitted = Number(response.headers["x-template-omitted-assets"] ?? 0);
  return { filename, omittedAssets: Number.isFinite(omitted) ? omitted : 0 };
}

/** Download the starter kit built from a reference package. */
export function downloadStarterKit(key: string) {
  return downloadZip(API_ROUTES.management.templates.starter(key), `jubiel-starter-custom_${key}.zip`);
}

/**
 * Download an installed package-shaped template. `omittedAssets` counts the
 * declared files the export could not include (see export-report.json).
 */
export function exportTemplate(id: number, key: string) {
  return downloadZip(API_ROUTES.management.templates.export(id), `jubiel-template-${key}.zip`);
}

/** Error responses of a blob request arrive as a Blob; read their message. */
export async function blobErrorMessage(error: unknown): Promise<string | null> {
  const data = (error as { response?: { data?: unknown } })?.response?.data;
  if (data instanceof Blob) {
    try {
      const parsed = JSON.parse(await data.text()) as { error?: { message?: string } };
      return parsed.error?.message ?? null;
    } catch {
      return null;
    }
  }
  return null;
}

